import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import type { Friend } from '@/types/friendship';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import { memo } from 'react';
import { Pressable, View } from 'react-native';

interface FriendRowProps {
  friend: Friend;
}

/**
 * A single friend row in the profile Friends tab. Extracted from the old
 * `FriendsList` so it can be a virtualized `FlatList` item and memoized: a
 * profile-level re-render (hover, tab switch) no longer repaints every row.
 * The avatar is decorative: the row's button label already names the friend.
 */
function FriendRowComponent({ friend }: FriendRowProps) {
  const displayName = friend.user.name || 'Unknown';
  const rawHandle = friend.user.username || friend.user.displayUsername || '';
  const normalizedHandle = rawHandle.replace(/^@+/, '');
  const handle = normalizedHandle ? `@${normalizedHandle}` : '';

  return (
    <Pressable
      className="flex-row items-center rounded-xl p-3 mb-2 bg-card border border-border"
      accessibilityRole="button"
      accessibilityLabel={`View ${displayName}'s profile`}
      onPress={() => {
        const targetHandle = (
          friend.user.username ||
          friend.user.displayUsername ||
          ''
        ).replace(/^@+/, '');
        router.push({
          pathname: '/(tabs)/profile/[username]',
          params: { username: targetHandle || friend.friendId },
        });
      }}
    >
      <UserAvatar
        uri={friend.user.image || friend.user.avatar}
        name={friend.user.name}
        size={48}
        decorative
        className="border border-border"
        style={{ marginRight: 12 }}
      />
      <View className="flex-1">
        <Text className="font-semibold text-base text-foreground">
          {displayName}
        </Text>
        {handle ? (
          <Text className="text-sm text-muted-foreground">{handle}</Text>
        ) : null}
        {(friend.friendCount != null || friend.checkInCount != null) && (
          <Text className="text-xs text-muted-foreground mt-0.5">
            {[
              friend.friendCount != null
                ? `${friend.friendCount} ${friend.friendCount === 1 ? 'friend' : 'friends'}${friend.mutualFriendCount ? ` (${friend.mutualFriendCount} mutual)` : ''}`
                : null,
              friend.checkInCount != null
                ? `${friend.checkInCount} ${friend.checkInCount === 1 ? 'check-in' : 'check-ins'}`
                : null,
            ]
              .filter(Boolean)
              .join('  ·  ')}
          </Text>
        )}
      </View>
      <Ionicons
        name="chevron-forward"
        size={16}
        className="text-muted-foreground"
      />
    </Pressable>
  );
}

export const FriendRow = memo(FriendRowComponent);
